import { Component, NgZone, OnInit } from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { HttpService } from 'app/services/http.service';
import { MessageService } from 'primeng/api';

/**
 * Promociones de la web de clientes: la franja de una línea arriba del Home.
 *
 * Acá se crean, se prenden y apagan, y se mide qué hicieron los usuarios con
 * ellas. A quién se le muestra cada una, cuándo vuelve después de que la cierran
 * y cuándo deja de tener sentido lo decide el backend
 * (apiweb/routesWeb/promocionesRoutes.js); esta pantalla solo lo configura.
 *
 * No se borran: se apagan, y conservan sus métricas.
 */
@Component({
  selector: 'app-promociones',
  templateUrl: './promociones.component.html',
  styleUrls: ['./promociones.component.scss']
})
export class PromocionesComponent implements OnInit {

  promociones: any[] = [];
  reglas: any = null;
  // Lo que vale cada marcador hoy, según el backend. Ej: { gratis_panico: '31-10-2026' }
  marcadores: any = {};
  cargando: boolean = false;

  modoCreaEdita: boolean = false;
  guardando: boolean = false;
  idEnEdicion: string = '';
  formulario: FormGroup;

  readonly LARGO_MAXIMO = 200;

  condiciones: any[] = [
    { label: 'Nunca: se muestra mientras esté activa', value: 'ninguna' },
    { label: 'Cuando TODAS las instalaciones que ve el usuario ya tienen botón de pánico', value: 'panico_contratado' }
  ];

  constructor(
    private messageService: MessageService,
    private formBuilder: FormBuilder,
    private http: HttpService,
    private ngZone: NgZone,
  ) {
    this.formulario = this.formBuilder.group({
      Nombre: ['', Validators.required],
      Texto: ['', [Validators.required, Validators.maxLength(this.LARGO_MAXIMO)]],
      TextoBoton: ['Conocer más'],
      Url: ['', Validators.required],
      ParaInternos: [true],
      ParaExternos: [true],
      CondicionSalida: ['ninguna'],
      FechaInicio: [null],
      FechaFin: [null],
      Prioridad: [0],
      Activa: [false]
    });
  }

  async ngOnInit(): Promise<void> {
    await this.carga();
  }

  async carga(): Promise<void> {
    this.cargando = true;
    const respuesta = await this.http.getPromocionesService();
    this.promociones = (respuesta && respuesta.promociones) ? respuesta.promociones : [];
    this.reglas = (respuesta && respuesta.reglas) ? respuesta.reglas : null;
    this.marcadores = (respuesta && respuesta.marcadores) ? respuesta.marcadores : {};
    this.cargando = false;
    // El target es esnext: sin esto la vista no se entera de lo que llegó.
    this.ngZone.run(() => { });
  }

  get nombresMarcadores(): string[] {
    return Object.keys(this.marcadores);
  }

  // El texto como lo vería un usuario hoy. Un marcador que el backend no conoce se
  // deja tal cual, igual que lo haría él (y además no deja guardarlo).
  conMarcadores(texto: string): string {
    return String(texto || '').replace(/\{([a-z_]+)\}/g, (todo, nombre) =>
      this.marcadores[nombre] ? this.marcadores[nombre] : todo);
  }

  get valor(): any {
    return this.formulario.getRawValue();
  }

  // Porcentaje sobre los usuarios que la vieron. Sin vistas no hay tasa.
  tasa(parte: number, total: number): string {
    if (!total) return '-';
    return Math.round((parte / total) * 100) + '%';
  }

  audiencia(item: any): string {
    const a = item.Audiencia || [];
    if (a.includes('INTERNO') && a.includes('EXTERNO')) return 'Empresas y mandantes';
    if (a.includes('INTERNO')) return 'Empresas de seguridad';
    if (a.includes('EXTERNO')) return 'Mandantes';
    return '-';
  }

  // "Activa" en la tabla no basta: una activa fuera de sus fechas no se muestra.
  estado(item: any): { texto: string, severidad: string } {
    const ahora = new Date();
    if (!item.Activa) return { texto: 'apagada', severidad: 'warning' };
    if (item.FechaInicio && new Date(item.FechaInicio) > ahora) return { texto: 'programada', severidad: 'info' };
    if (item.FechaFin && new Date(item.FechaFin) < ahora) return { texto: 'vencida', severidad: 'danger' };
    return { texto: 'mostrándose', severidad: 'success' };
  }

  nuevo(): void {
    this.idEnEdicion = '';
    this.modoCreaEdita = true;
    this.formulario.reset({
      Nombre: '', Texto: '', TextoBoton: 'Conocer más', Url: '',
      ParaInternos: true, ParaExternos: true, CondicionSalida: 'ninguna',
      FechaInicio: null, FechaFin: null, Prioridad: 0, Activa: false
    });
  }

  editar(item: any): void {
    this.idEnEdicion = item._id;
    this.modoCreaEdita = true;
    const a = item.Audiencia || [];
    this.formulario.reset({
      Nombre: item.Nombre,
      Texto: item.Texto,
      TextoBoton: item.TextoBoton || 'Conocer más',
      Url: item.Url,
      ParaInternos: a.includes('INTERNO'),
      ParaExternos: a.includes('EXTERNO'),
      CondicionSalida: item.CondicionSalida || 'ninguna',
      FechaInicio: item.FechaInicio ? new Date(item.FechaInicio) : null,
      FechaFin: item.FechaFin ? new Date(item.FechaFin) : null,
      Prioridad: item.Prioridad || 0,
      Activa: !!item.Activa
    });
  }

  cancelar(): void {
    this.modoCreaEdita = false;
    this.idEnEdicion = '';
  }

  guardar(): void {
    if (this.guardando) return;
    const v = this.valor;

    const audiencia = [];
    if (v.ParaInternos) audiencia.push('INTERNO');
    if (v.ParaExternos) audiencia.push('EXTERNO');

    if (!audiencia.length) {
      this.avisa('Falta la audiencia', 'Elige empresas de seguridad, mandantes o ambos.');
      return;
    }
    if (!/^https:\/\//i.test((v.Url || '').trim())) {
      this.avisa('URL inválida', 'Debe empezar con https://');
      return;
    }

    // Días completos: desde el comienzo del día de inicio hasta el final del de término.
    const inicio = v.FechaInicio ? new Date(v.FechaInicio) : null;
    const fin = v.FechaFin ? new Date(v.FechaFin) : null;
    if (inicio) inicio.setHours(0, 0, 0, 0);
    if (fin) fin.setHours(23, 59, 59, 999);

    const datos = {
      Nombre: v.Nombre,
      Texto: v.Texto,
      TextoBoton: v.TextoBoton,
      Url: (v.Url || '').trim(),
      Audiencia: audiencia,
      CondicionSalida: v.CondicionSalida,
      FechaInicio: inicio,
      FechaFin: fin,
      Prioridad: v.Prioridad || 0,
      Activa: !!v.Activa
    };

    this.guardando = true;
    const peticion = this.idEnEdicion
      ? this.http.updatePromocionService(this.idEnEdicion, datos)
      : this.http.newPromocionService(datos);

    peticion.subscribe(async () => {
      this.guardando = false;
      this.modoCreaEdita = false;
      this.messageService.add({ severity: 'success', summary: 'Guardada', detail: v.Nombre });
      await this.carga();
    }, (err) => {
      this.guardando = false;
      this.avisa('No se pudo guardar', (err && err.error && err.error.error) || 'Error inesperado');
      this.ngZone.run(() => { });
    });
  }

  private avisa(titulo: string, detalle: string): void {
    this.messageService.add({ severity: 'error', summary: titulo, detail: detalle });
  }
}
